import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { DeleteConfirmDialog } from '../../components/common/DeleteConfirmDialog';
import { GtSampleCard } from '../../components/common/GtSampleCard';
import { LoadMoreBar } from '../../components/common/LoadMoreBar';
import { TagEditor } from '../../components/common/TagEditor';
import { usePagedGtSamples } from '../../hooks/usePagedGtSamples';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, isSampleReady } from '../../types';

function countBy<T>(
  items: T[],
  key: (item: T) => string | number | null | undefined
): Array<{ label: string; count: number }> {
  const map = new Map<string, number>();
  for (const item of items) {
    const k = String(key(item) ?? '—');
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

const DistributionBar: React.FC<{
  title: string;
  entries: Array<{ label: string; count: number }>;
  total: number;
}> = ({ title, entries, total }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
    <span
      style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {title}
    </span>
    {entries.map(({ label, count }) => (
      <span
        key={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          padding: '1px 6px',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
        <span style={{ color: 'var(--text-tertiary)' }}>×</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{count}</span>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
          ({Math.round((count / total) * 100)}%)
        </span>
      </span>
    ))}
  </div>
);

export const DatasetDetailPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<EvalGtDataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'samples' | 'add'>('samples');
  const [locking, setLocking] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState('');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterIngestedBy, setFilterIngestedBy] = useState('');

  const samplesList = usePagedGtSamples(token ?? '', { dataset_id: datasetId }, !!datasetId);
  const inferences = samplesList.samples;

  const unassignedList = usePagedGtSamples(
    token ?? '',
    {
      unassigned: true,
      store_id: filterStoreId ? Number(filterStoreId) : undefined,
      chain_id: filterChainId ? Number(filterChainId) : undefined,
      ingested_by: filterIngestedBy || undefined,
    },
    activeTab === 'add'
  );
  const unassigned = unassignedList.samples;
  const loadingUnassigned = unassignedList.loading;

  useEffect(() => {
    if (!datasetId) return;
    EvalApiService.getDataset(token ?? '', datasetId).then((ds) => {
      setDataset(ds);
      setLoading(false);
    });
  }, [datasetId, token]);

  const handleAddSelected = async () => {
    if (!datasetId || selected.size === 0) return;
    setAdding(true);
    try {
      const { sample_count } = await EvalApiService.addInferencesToDataset(token ?? '', datasetId, {
        gt_sample_ids: Array.from(selected),
      });
      setDataset((prev) => (prev ? { ...prev, sample_count } : prev));
      setSelected(new Set());
      samplesList.reload();
      unassignedList.reload();
    } finally {
      setAdding(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleLock = async () => {
    if (!dataset) return;
    setLocking(true);
    const updated = await EvalApiService.lockDataset(token ?? '', dataset.dataset_id);
    setDataset(updated);
    setActiveTab('samples');
    setLocking(false);
  };

  const handleUnlock = async () => {
    if (!dataset) return;
    const confirmed = window.confirm(
      'Unlock this dataset? Adding or removing samples afterwards will mark all completed runs on it as OUTDATED.'
    );
    if (!confirmed) return;
    setLocking(true);
    try {
      const updated = await EvalApiService.unlockDataset(token ?? '', dataset.dataset_id);
      setDataset(updated);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error unlocking dataset');
    } finally {
      setLocking(false);
    }
  };

  const handleDelete = async () => {
    if (!dataset) return;
    try {
      await EvalApiService.deleteDataset(token ?? '', dataset.dataset_id);
      navigate('/eval/datasets');
    } catch (err) {
      setShowDeleteDialog(false);
      window.alert(err instanceof Error ? err.message : 'Error deleting dataset');
    }
  };

  const handleRemoveSample = async (gtSampleId: string) => {
    if (!dataset) return;
    const confirmed = window.confirm(
      'Remove this sample from the dataset? All completed runs on this dataset will be marked as OUTDATED.'
    );
    if (!confirmed) return;
    try {
      await EvalApiService.removeInferenceFromDataset(token ?? '', dataset.dataset_id, gtSampleId);
      setDataset((prev) => (prev ? { ...prev, sample_count: prev.sample_count - 1 } : prev));
      samplesList.reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error removing sample');
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>Dataset not found.</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title">{dataset.dataset_name}</div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: dataset.status === 'locked' ? 'var(--positive)' : 'var(--warning)',
              }}
            >
              {dataset.status.toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {dataset.sample_count} samples
            </span>
          </div>
          {dataset.description && <div className="eval-page-sub">{dataset.description}</div>}
          <div style={{ marginTop: 8 }}>
            <TagEditor
              token={token ?? ''}
              tags={dataset.tags}
              disabled={dataset.status === 'locked'}
              onChange={async (tags) => {
                const updated = await EvalApiService.patchDataset(token ?? '', dataset.dataset_id, {
                  tags,
                });
                setDataset(updated);
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {dataset.status === 'draft' ? (
            <button className="eval-btn" onClick={handleLock} disabled={locking}>
              {locking ? 'Locking...' : 'Lock Dataset'}
            </button>
          ) : (
            <button
              className="eval-btn"
              onClick={handleUnlock}
              disabled={locking}
              title="Unlock to add or remove samples. Changing samples marks all completed runs as outdated."
            >
              {locking ? 'Unlocking...' : 'Unlock Dataset'}
            </button>
          )}
          <button
            className="eval-btn-danger"
            onClick={() => setShowDeleteDialog(true)}
            title="Delete this dataset. Its samples return to the unassigned pool."
          >
            Delete
          </button>
        </div>
      </div>

      {showDeleteDialog && (
        <DeleteConfirmDialog
          title="Delete Dataset"
          description={`This will permanently delete dataset "${dataset.dataset_name}"`}
          itemId={dataset.dataset_id}
          consequence={`Its ${dataset.sample_count} sample${dataset.sample_count === 1 ? '' : 's'} will return to the unassigned pool.`}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}

      <div className="eval-tabs">
        <button
          className={`eval-tab ${activeTab === 'samples' ? 'active' : ''}`}
          onClick={() => setActiveTab('samples')}
        >
          Samples ({samplesList.total})
        </button>
        <button
          className={`eval-tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
          disabled={dataset.status === 'locked'}
          title={dataset.status === 'locked' ? 'Unlock the dataset to add samples' : undefined}
        >
          Add Samples
        </button>
      </div>

      {activeTab === 'samples' && inferences.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginBottom: 16,
            padding: '10px 12px',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          <DistributionBar
            title="Chains:"
            entries={countBy(inferences, (i) => i.chain_id)}
            total={inferences.length}
          />
          <DistributionBar
            title="Stores:"
            entries={countBy(inferences, (i) => i.store_id)}
            total={inferences.length}
          />
        </div>
      )}

      {activeTab === 'samples' && (
        <div className="eval-inference-grid">
          {inferences.map((inf) => (
            <GtSampleCard
              key={inf.gt_sample_id}
              sample={inf}
              onClick={() => navigate(`/eval/gt-samples/${inf.gt_sample_id}`)}
              actions={
                <>
                  <button
                    className="eval-btn"
                    style={{ padding: '2px 7px', fontSize: 10 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/gt/audit/${inf.gt_sample_id}`, '_blank');
                    }}
                  >
                    Edit
                  </button>
                  {dataset.status === 'draft' && (
                    <button
                      className="eval-btn"
                      style={{ padding: '2px 7px', fontSize: 10, color: 'var(--danger)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSample(inf.gt_sample_id);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </>
              }
            />
          ))}
          {inferences.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
              No samples in this dataset yet.
            </div>
          )}
        </div>
      )}

      {activeTab === 'samples' && (
        <LoadMoreBar
          shown={inferences.length}
          total={samplesList.total}
          loading={samplesList.loading}
          onLoadMore={samplesList.loadMore}
        />
      )}

      {activeTab === 'add' && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <input
              className="eval-form-input"
              placeholder="store_id"
              value={filterStoreId}
              onChange={(e) => setFilterStoreId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="chain_id"
              value={filterChainId}
              onChange={(e) => setFilterChainId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="ingested by"
              value={filterIngestedBy}
              onChange={(e) => setFilterIngestedBy(e.target.value)}
              style={{ width: 160, fontSize: 12 }}
            />
            <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary)' }}>
              {loadingUnassigned ? 'Loading…' : `${unassignedList.total} unassigned`}
            </div>
            {selected.size > 0 && (
              <button className="eval-btn" onClick={handleAddSelected} disabled={adding}>
                {adding ? 'Adding…' : `Add Selected (${selected.size})`}
              </button>
            )}
          </div>
          <div className="eval-inference-grid">
            {unassigned.map((inf) => (
              <GtSampleCard
                key={inf.gt_sample_id}
                sample={inf}
                onClick={() => toggleSelect(inf.gt_sample_id)}
                selected={selected.has(inf.gt_sample_id)}
                disabled={!isSampleReady(inf)}
                showIngestionInfo
                showStatusBadges
              />
            ))}
            {!loadingUnassigned && unassigned.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
                No unassigned inferences available.
              </div>
            )}
          </div>
          <LoadMoreBar
            shown={unassigned.length}
            total={unassignedList.total}
            loading={loadingUnassigned}
            onLoadMore={unassignedList.loadMore}
          />
        </div>
      )}
    </div>
  );
};
